import { STATUS } from "./statuses";
import { DOWNLOAD_TYPES } from "./downloads";

/**
 * Product Master — single source of truth for Products, Shop, Range pages and quotes.
 * priceVisible = false for all items initially (Quote Mode).
 * Only verified catalogue specifications are recorded — never fabricate certifications or performance claims.
 */

const defaultDownloads = () =>
  DOWNLOAD_TYPES.map((d) => ({
    id: d.id,
    name: d.name,
    status: "Coming Soon",
    url: null,
    available: false,
  }));

const lightingAppsIndoor = [
  "Homes and apartments",
  "Offices and retail",
  "Utility areas",
  "General indoor lighting",
];

const bulbFeatures = [
  "Up to 85% energy saving (catalogue claim)",
  "Long life",
  "Eye comfort design intent",
  "Wide voltage 100V – 300V AC",
  "B22 base",
  "Daylight 6500K",
];

const panelFeatures = [
  "Up to 85% energy saving (catalogue claim)",
  "Long life",
  "Eye comfort design intent",
  "Wide voltage 100V – 300V AC",
  "Round form factor",
  "DOB / driver technology options on request",
  "Daylight 6500K",
];

const floodFeatures = [
  "Up to 85% energy saving (catalogue claim)",
  "Long life",
  "High brightness design intent",
  "Gamma / Lens optic options",
  "All-weather construction intent",
  "220–240V AC",
  "Daylight 6500K",
];

function bulbVariant({ sku, slug, watts, image, related }) {
  return {
    sku,
    slug,
    familyId: "led-bulbs",
    name: `DEBSUN LED Bulb — ${watts}`,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription: `${watts} daylight LED bulb with B22 base and wide-voltage operation.`,
    description: `DEBSUN ${watts} LED bulb for efficient indoor lighting. Daylight 6500K colour temperature, B22 base and wide voltage 100V – 300V AC.`,
    applications: lightingAppsIndoor,
    features: bulbFeatures,
    specifications: [
      { label: "Rated Power", value: watts },
      { label: "Colour Temperature", value: "6500 K Daylight" },
      { label: "Base", value: "B22" },
      { label: "Input Voltage", value: "100V – 300V AC (wide voltage)" },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Bulb", "Lighting", watts],
    searchKeywords: [
      "led bulb",
      watts.toLowerCase(),
      "b22",
      "6500k",
      "daylight",
      "debsun bulb",
    ],
    price: null,
    seo: {
      title: `DEBSUN LED Bulb ${watts} | Product Detail`,
      description: `DEBSUN ${watts} LED bulb — 6500K daylight, B22 base. Request a quote.`,
    },
  };
}

function bulbHpVariant({ sku, slug, watts, image, related }) {
  return {
    ...bulbVariant({ sku, slug, watts, image, related }),
    familyId: "led-bulbs-hp",
    name: `DEBSUN High-Power LED Bulb — ${watts}`,
    shortDescription: `${watts} high-power daylight LED bulb with B22 base.`,
    description: `DEBSUN ${watts} high-power LED bulb for larger indoor spaces. Daylight 6500K colour temperature, B22 base and wide voltage 100V – 300V AC.`,
    applications: [
      "Large rooms and halls",
      "Workshops",
      "Commercial interiors",
      "High-ceiling indoor areas",
    ],
    tags: ["LED", "Bulb", "High Power", "Lighting", watts],
    seo: {
      title: `DEBSUN High-Power LED Bulb ${watts} | Product Detail`,
      description: `DEBSUN ${watts} high-power LED bulb — 6500K daylight, B22 base. Request a quote.`,
    },
  };
}

function panelVariant({ sku, slug, watts, size, image, related }) {
  return {
    sku,
    slug,
    familyId: "panel-lights",
    name: `DEBSUN Round LED Panel — ${watts}`,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription: `${watts} (${size}) round LED panel light, 6500K daylight.`,
    description: `DEBSUN ${watts} round LED panel light (${size}) for clean indoor illumination. Daylight 6500K colour temperature with wide voltage 100V – 300V AC. DOB or driver configuration available — confirm with engineering.`,
    applications: [
      "Offices and workspaces",
      "Homes and corridors",
      "Retail interiors",
      "Commercial ceilings",
    ],
    features: panelFeatures,
    specifications: [
      { label: "Rated Power", value: watts },
      { label: "Size", value: size },
      { label: "Shape", value: "Round" },
      { label: "Colour Temperature", value: "6500 K Daylight" },
      { label: "Input Voltage", value: "100V – 300V AC (wide voltage)" },
      {
        label: "Technology Options",
        value: "DOB / High-performance driver — confirm with Engineering",
      },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Panel", "Round", "Lighting", watts],
    searchKeywords: [
      "led panel",
      "round panel",
      watts.toLowerCase(),
      size.toLowerCase(),
      "6500k",
      "daylight",
    ],
    price: null,
    seo: {
      title: `DEBSUN Round LED Panel ${watts} (${size}) | Product Detail`,
      description: `DEBSUN ${watts} round LED panel (${size}) — 6500K daylight. Request a quote.`,
    },
  };
}

function floodVariant({ sku, slug, watts, image, related }) {
  return {
    sku,
    slug,
    familyId: "flood-lights",
    name: `DEBSUN LED Flood Light — ${watts}`,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription: `${watts} LED flood light — Gamma / Lens optics, 6500K daylight.`,
    description: `DEBSUN ${watts} LED flood light for industrial and outdoor area lighting. Available in Gamma (wide beam) and Lens (focused beam) models. Daylight 6500K with all-weather construction intent.`,
    applications: [
      "Loading docks and yards",
      "Outdoor storage areas",
      "Industrial approach roads",
      "Perimeter and facade wash",
    ],
    features: floodFeatures,
    specifications: [
      { label: "Rated Power", value: watts },
      { label: "Colour Temperature", value: "6500 K Daylight" },
      { label: "Optics", value: "Gamma Model / Lens Model" },
      { label: "Durability Intent", value: "All weather" },
      { label: "Input Voltage", value: "220–240V AC" },
      { label: "Configuration", value: "Contact Engineering for optic selection" },
    ],
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Floodlight", "Outdoor", "Gamma", "Lens", watts],
    searchKeywords: [
      "flood light",
      "floodlight",
      watts.toLowerCase(),
      "gamma",
      "lens",
      "6500k",
      "outdoor lighting",
    ],
    price: null,
    seo: {
      title: `DEBSUN LED Flood Light ${watts} | Product Detail`,
      description: `DEBSUN ${watts} LED flood light — Gamma / Lens optics, 6500K daylight. Request a quote.`,
    },
  };
}

function squarePanelVariant({ sku, slug, watts, size, image, related }) {
  return {
    sku,
    slug,
    familyId: "panel-lights-square",
    name: `DEBSUN Square LED Panel — ${watts}`,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription: `${watts} (${size}) square LED panel light, 6500K daylight.`,
    description: `DEBSUN ${watts} square LED panel light (${size}) for clean indoor illumination. Daylight 6500K colour temperature with wide voltage 100V – 300V AC. DOB or driver configuration available — confirm with engineering.`,
    applications: [
      "Offices and workspaces",
      "Homes and corridors",
      "Retail interiors",
      "Commercial ceilings",
    ],
    features: [
      "Up to 85% energy saving (catalogue claim)",
      "Long life",
      "Eye comfort design intent",
      "Wide voltage 100V – 300V AC",
      "Square form factor",
      "DOB / driver technology options on request",
      "Daylight 6500K",
    ],
    specifications: [
      { label: "Rated Power", value: watts },
      { label: "Size", value: size },
      { label: "Shape", value: "Square" },
      { label: "Colour Temperature", value: "6500 K Daylight" },
      { label: "Input Voltage", value: "100V – 300V AC (wide voltage)" },
      {
        label: "Technology Options",
        value: "DOB / High-performance driver — confirm with Engineering",
      },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Panel", "Square", "Lighting", watts],
    searchKeywords: [
      "led panel",
      "square panel",
      watts.toLowerCase(),
      size.toLowerCase(),
      "6500k",
      "daylight",
    ],
    price: null,
    seo: {
      title: `DEBSUN Square LED Panel ${watts} (${size}) | Product Detail`,
      description: `DEBSUN ${watts} square LED panel (${size}) — 6500K daylight. Request a quote.`,
    },
  };
}

function streetVariant({ sku, slug, watts, image, related }) {
  return {
    sku,
    slug,
    familyId: "street-lights",
    name: `DEBSUN LED Street Light — ${watts}`,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription: `${watts} LED street light — 6500K daylight, all-weather intent.`,
    description: `DEBSUN ${watts} LED street light for roads, campuses and outdoor pathways. Daylight 6500K colour temperature with all-weather construction intent.`,
    applications: [
      "Roads and streets",
      "Campus pathways",
      "Industrial approach roads",
      "Parking and perimeter lighting",
    ],
    features: [
      "6500K Daylight",
      "All-weather construction intent",
      "Outdoor continuous-duty design intent",
      "Reliable performance design intent",
    ],
    specifications: [
      { label: "Rated Power", value: watts },
      { label: "Colour Temperature", value: "6500 K Daylight" },
      { label: "Durability Intent", value: "All weather" },
      { label: "Application", value: "Street / pathway lighting" },
      { label: "Configuration", value: "Contact Engineering for mounting options" },
    ],
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Street Light", "Outdoor", "Lighting", watts],
    searchKeywords: [
      "street light",
      "streetlight",
      watts.toLowerCase(),
      "6500k",
      "road lighting",
      "pathway",
    ],
    price: null,
    seo: {
      title: `DEBSUN LED Street Light ${watts} | Product Detail`,
      description: `DEBSUN ${watts} LED street light — 6500K daylight. Request a quote.`,
    },
  };
}

function tubeVariant({
  sku,
  slug,
  watts,
  name,
  shortDescription,
  description,
  image,
  related,
  specifications,
  features,
  tags,
  searchKeywords,
}) {
  return {
    sku,
    slug,
    familyId: "tube-lights",
    name,
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: watts,
    shortDescription,
    description,
    applications: [
      "Homes, kitchens and offices",
      "Showrooms and cabinets",
      "Factories and warehouses",
      "Workshops and assembly areas",
    ],
    features,
    specifications,
    images: [image],
    downloads: defaultDownloads(),
    relatedProducts: related,
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags,
    searchKeywords,
    price: null,
    seo: {
      title: `${name} | DEBSUN Product Detail`,
      description: `${shortDescription} Request a quote.`,
    },
  };
}

export const PRODUCTS = [
  // —— LED Bulbs 7W–15W ——
  bulbVariant({
    sku: "DB-LED-BLB-07",
    slug: "7w",
    watts: "7W",
    image: "/products/lighting/led-bulbs/variants/7w.png",
    related: ["DB-LED-BLB-09", "DB-LED-BLB-12", "DB-LED-BLB-15"],
  }),
  bulbVariant({
    sku: "DB-LED-BLB-09",
    slug: "9w",
    watts: "9W",
    image: "/products/lighting/led-bulbs/variants/9w.png",
    related: ["DB-LED-BLB-07", "DB-LED-BLB-12", "DB-LED-BLB-15"],
  }),
  bulbVariant({
    sku: "DB-LED-BLB-12",
    slug: "12w",
    watts: "12W",
    image: "/products/lighting/led-bulbs/variants/12w.png",
    related: ["DB-LED-BLB-07", "DB-LED-BLB-09", "DB-LED-BLB-15"],
  }),
  bulbVariant({
    sku: "DB-LED-BLB-15",
    slug: "15w",
    watts: "15W",
    image: "/products/lighting/led-bulbs/variants/15w.png",
    related: ["DB-LED-BLB-07", "DB-LED-BLB-09", "DB-LED-BLB-12"],
  }),

  // —— High-power LED Bulbs 20W–50W ——
  bulbHpVariant({
    sku: "DB-LED-BLB-20",
    slug: "20w",
    watts: "20W",
    image: "/products/lighting/led-bulbs-hp/variants/20w.png",
    related: ["DB-LED-BLB-30", "DB-LED-BLB-40", "DB-LED-BLB-50"],
  }),
  bulbHpVariant({
    sku: "DB-LED-BLB-30",
    slug: "30w",
    watts: "30W",
    image: "/products/lighting/led-bulbs-hp/variants/30w.png",
    related: ["DB-LED-BLB-20", "DB-LED-BLB-40", "DB-LED-BLB-50"],
  }),
  bulbHpVariant({
    sku: "DB-LED-BLB-40",
    slug: "40w",
    watts: "40W",
    image: "/products/lighting/led-bulbs-hp/variants/40w.png",
    related: ["DB-LED-BLB-20", "DB-LED-BLB-30", "DB-LED-BLB-50"],
  }),
  bulbHpVariant({
    sku: "DB-LED-BLB-50",
    slug: "50w",
    watts: "50W",
    image: "/products/lighting/led-bulbs-hp/variants/50w.png",
    related: ["DB-LED-BLB-20", "DB-LED-BLB-30", "DB-LED-BLB-40"],
  }),

  // —— Round Panel Lights ——
  panelVariant({
    sku: "DB-LED-PL-08",
    slug: "8w",
    watts: "8W",
    size: '4"',
    image: "/products/lighting/panel-lights/variants/8w.png",
    related: ["DB-LED-PL-12", "DB-LED-PL-15", "DB-LED-PL-22"],
  }),
  panelVariant({
    sku: "DB-LED-PL-12",
    slug: "12w",
    watts: "12W",
    size: '5"',
    image: "/products/lighting/panel-lights/variants/12w.png",
    related: ["DB-LED-PL-08", "DB-LED-PL-15", "DB-LED-PL-22"],
  }),
  panelVariant({
    sku: "DB-LED-PL-15",
    slug: "15w",
    watts: "15W",
    size: '6"',
    image: "/products/lighting/panel-lights/variants/15w.png",
    related: ["DB-LED-PL-08", "DB-LED-PL-12", "DB-LED-PL-22"],
  }),
  panelVariant({
    sku: "DB-LED-PL-22",
    slug: "22w",
    watts: "22W",
    size: '8"',
    image: "/products/lighting/panel-lights/variants/22w.png",
    related: ["DB-LED-PL-08", "DB-LED-PL-12", "DB-LED-PL-15"],
  }),

  // —— Flood Lights (replaces prior Corona FL-200 placeholder identity) ——
  floodVariant({
    sku: "DB-LED-FL-050",
    slug: "50w",
    watts: "50W",
    image: "/products/lighting/flood-lights/variants/50w.png",
    related: ["DB-LED-FL-100", "DB-LED-FL-200", "DB-LED-HB-150"],
  }),
  floodVariant({
    sku: "DB-LED-FL-100",
    slug: "100w",
    watts: "100W",
    image: "/products/lighting/flood-lights/variants/100w.png",
    related: ["DB-LED-FL-050", "DB-LED-FL-200", "DB-LED-HB-150"],
  }),
  floodVariant({
    sku: "DB-LED-FL-200",
    slug: "200w",
    watts: "200W",
    image: "/products/lighting/flood-lights/variants/200w.png",
    related: ["DB-LED-FL-050", "DB-LED-FL-100", "DB-LED-HB-150"],
  }),

  // —— Square Panel Lights ——
  squarePanelVariant({
    sku: "DB-LED-PLS-08",
    slug: "8w",
    watts: "8W",
    size: '4"',
    image: "/products/lighting/panel-lights-square/variants/8w.png",
    related: ["DB-LED-PLS-12", "DB-LED-PLS-15", "DB-LED-PLS-22"],
  }),
  squarePanelVariant({
    sku: "DB-LED-PLS-12",
    slug: "12w",
    watts: "12W",
    size: '5"',
    image: "/products/lighting/panel-lights-square/variants/12w.png",
    related: ["DB-LED-PLS-08", "DB-LED-PLS-15", "DB-LED-PLS-22"],
  }),
  squarePanelVariant({
    sku: "DB-LED-PLS-15",
    slug: "15w",
    watts: "15W",
    size: '6"',
    image: "/products/lighting/panel-lights-square/variants/15w.png",
    related: ["DB-LED-PLS-08", "DB-LED-PLS-12", "DB-LED-PLS-22"],
  }),
  squarePanelVariant({
    sku: "DB-LED-PLS-22",
    slug: "22w",
    watts: "22W",
    size: '8"',
    image: "/products/lighting/panel-lights-square/variants/22w.png",
    related: ["DB-LED-PLS-08", "DB-LED-PLS-12", "DB-LED-PLS-15"],
  }),

  // —— Street Lights ——
  streetVariant({
    sku: "DB-LED-SL-024",
    slug: "24w",
    watts: "24W",
    image: "/products/lighting/street-lights/variants/24w.png",
    related: ["DB-LED-SL-030", "DB-LED-SL-050", "DB-LED-SL-100"],
  }),
  streetVariant({
    sku: "DB-LED-SL-030",
    slug: "30w",
    watts: "30W",
    image: "/products/lighting/street-lights/variants/30w.png",
    related: ["DB-LED-SL-024", "DB-LED-SL-050", "DB-LED-SL-100"],
  }),
  streetVariant({
    sku: "DB-LED-SL-050",
    slug: "50w",
    watts: "50W",
    image: "/products/lighting/street-lights/variants/50w.png",
    related: ["DB-LED-SL-024", "DB-LED-SL-030", "DB-LED-SL-100"],
  }),
  streetVariant({
    sku: "DB-LED-SL-100",
    slug: "100w",
    watts: "100W",
    image: "/products/lighting/street-lights/variants/100w.png",
    related: ["DB-LED-SL-024", "DB-LED-SL-030", "DB-LED-SL-050"],
  }),

  // —— Tube Lights ——
  tubeVariant({
    sku: "DB-LED-TB-05AC",
    slug: "5w-ac",
    watts: "5W",
    name: "DEBSUN 5W AC LED Tube Light — 1 Feet",
    shortDescription: "5W AC LED 1-foot tube light for compact residential and industrial use.",
    description:
      "DEBSUN 5W AC LED 1-foot tube light for homes, offices, cabinets and light industrial spaces. AC operation 220V – 240V. Confirm mounting and driver configuration with engineering.",
    image: "/products/lighting/tube-lights/variants/5w-ac.png",
    related: ["DB-LED-TB-05DC", "DB-LED-TB-20AC", "DB-LED-TB-36AC"],
    features: [
      "AC operation 220V – 240V",
      "Energy efficient design intent",
      "Flicker-free light design intent",
      "Stable & reliable performance design intent",
      "Easy installation",
    ],
    specifications: [
      { label: "Rated Power", value: "5W" },
      { label: "Length", value: "1 feet" },
      { label: "Operation", value: "AC 220V – 240V" },
      { label: "Colour Temperature", value: "6500 K Daylight (range catalogue)" },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    tags: ["LED", "Tube Light", "AC", "1 Feet", "5W"],
    searchKeywords: ["tube light", "5w", "ac", "1 feet", "led tube"],
  }),
  tubeVariant({
    sku: "DB-LED-TB-05DC",
    slug: "5w-dc",
    watts: "5W",
    name: "DEBSUN 5W DC LED Tube Light — 1 Feet",
    shortDescription: "5W DC LED 1-foot tube light for low-voltage applications.",
    description:
      "DEBSUN 5W DC LED 1-foot tube light for compact residential, commercial and workspace use. DC operation 12V – 48V. Confirm mounting and driver configuration with engineering.",
    image: "/products/lighting/tube-lights/variants/5w-dc.png",
    related: ["DB-LED-TB-05AC", "DB-LED-TB-20AC", "DB-LED-TB-36AC"],
    features: [
      "DC operation 12V – 48V",
      "Low power consumption design intent",
      "Flicker-free light design intent",
      "Stable & reliable performance design intent",
      "Easy installation",
    ],
    specifications: [
      { label: "Rated Power", value: "5W" },
      { label: "Length", value: "1 feet" },
      { label: "Operation", value: "DC 12V – 48V" },
      { label: "Colour Temperature", value: "6500 K Daylight (range catalogue)" },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    tags: ["LED", "Tube Light", "DC", "1 Feet", "5W"],
    searchKeywords: ["tube light", "5w", "dc", "1 feet", "led tube", "12v"],
  }),
  tubeVariant({
    sku: "DB-LED-TB-20AC",
    slug: "20w-ac",
    watts: "20W",
    name: "DEBSUN 20W AC LED Tube Light — 4 Feet",
    shortDescription: "20W AC LED 4-foot tube light with HPF driver base.",
    description:
      "DEBSUN 20W AC LED 4-foot tube light (T5 / HPF driver base per catalogue) for residential and industrial spaces. AC operation 220V – 240V. Brightness and life figures on catalogue materials — confirm project requirements with engineering.",
    image: "/products/lighting/tube-lights/variants/20w-ac.png",
    related: ["DB-LED-TB-36AC", "DB-LED-TB-05AC", "DB-LED-TB-05DC"],
    features: [
      "Premium LED chips (catalogue claim)",
      "HPF driver for high efficiency (catalogue)",
      "Wide voltage protection (catalogue claim)",
      "Flicker free / instant start (catalogue claim)",
      "AC operation 220V – 240V",
      "4 feet length",
    ],
    specifications: [
      { label: "Rated Power", value: "20W" },
      { label: "Length", value: "4 feet" },
      { label: "Operation", value: "AC 220V – 240V" },
      { label: "Driver", value: "HPF driver base (catalogue)" },
      { label: "Typical Output", value: "2000+ lm (catalogue claim)" },
      { label: "Colour Temperature", value: "6500 K Daylight (range catalogue)" },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    tags: ["LED", "Tube Light", "AC", "4 Feet", "20W", "T5"],
    searchKeywords: ["tube light", "20w", "ac", "4 feet", "t5", "hpf"],
  }),
  tubeVariant({
    sku: "DB-LED-TB-36AC",
    slug: "36w-ac",
    watts: "36W",
    name: "DEBSUN 36W AC LED Tube Light — 4 Feet",
    shortDescription: "36W AC LED 4-foot tube light with HPF driver base.",
    description:
      "DEBSUN 36W AC LED 4-foot tube light (T5 / HPF driver base per catalogue) for residential and industrial spaces. AC operation 220V – 240V. Brightness and life figures on catalogue materials — confirm project requirements with engineering.",
    image: "/products/lighting/tube-lights/variants/36w-ac.png",
    related: ["DB-LED-TB-20AC", "DB-LED-TB-05AC", "DB-LED-TB-05DC"],
    features: [
      "Premium LED chips (catalogue claim)",
      "HPF driver for high efficiency (catalogue)",
      "Wide voltage protection (catalogue claim)",
      "Flicker free / instant start (catalogue claim)",
      "AC operation 220V – 240V",
      "4 feet length",
    ],
    specifications: [
      { label: "Rated Power", value: "36W" },
      { label: "Length", value: "4 feet" },
      { label: "Operation", value: "AC 220V – 240V" },
      { label: "Driver", value: "HPF driver base (catalogue)" },
      { label: "Typical Output", value: "3600+ lm (catalogue claim)" },
      { label: "Colour Temperature", value: "6500 K Daylight (range catalogue)" },
      { label: "Configuration", value: "Contact Engineering for project options" },
    ],
    tags: ["LED", "Tube Light", "AC", "4 Feet", "36W", "T5"],
    searchKeywords: ["tube light", "36w", "ac", "4 feet", "t5", "hpf"],
  }),

  // —— Existing industrial / platform products ——
  {
    sku: "DB-LED-HB-150",
    slug: "150w",
    familyId: "aurora-highbay",
    name: "Aurora Highbay 150W",
    category: "smart-electronics",
    subcategory: "LED Lighting",
    brand: "DEBSUN",
    wattage: "150W",
    shortDescription:
      "Industrial LED highbay for factories, warehouses and production halls.",
    description:
      "Industrial LED highbay luminaire for factories, warehouses and production halls. Engineered for efficient illumination, durable housing and straightforward installation in demanding environments.",
    applications: [
      "Warehouses and logistics halls",
      "Manufacturing bays",
      "Steel and metallurgy sheds",
      "Automotive workshops",
    ],
    features: [
      "High-efficiency LED light engine",
      "Industrial-grade housing for continuous duty",
      "Interchangeable optic options",
      "Surge-protected driver stage",
      "Suitable for high mounting heights",
    ],
    specifications: [
      { label: "Rated Power", value: "150 W" },
      { label: "Colour Temperature", value: "Contact Engineering" },
      { label: "Enclosure Intent", value: "Industrial indoor / high-bay" },
      { label: "Mounting", value: "Suspension / bracket" },
      { label: "Optical Output", value: "Specification under validation" },
      { label: "Control Options", value: "On request" },
    ],
    images: [],
    downloads: defaultDownloads(),
    relatedProducts: ["DB-LED-FL-050", "DB-LED-FL-100", "DB-LED-FL-200"],
    status: STATUS.AVAILABLE,
    quoteEnabled: true,
    priceVisible: false,
    available: true,
    featured: false,
    tags: ["LED", "Highbay", "Lighting", "Industrial"],
    searchKeywords: [
      "highbay",
      "led lighting",
      "warehouse light",
      "industrial luminaire",
      "aurora",
    ],
    price: null,
    seo: {
      title: "Aurora Highbay 150W | DEBSUN Product Detail",
      description:
        "DEBSUN Aurora Highbay 150W industrial LED luminaire. Request a quote.",
    },
  },

  // —— Platform / research ——
  {
    sku: "DB-GENESYS-PLATFORM",
    slug: "platform",
    familyId: "genesys",
    name: "DEBSUN GENESYS",
    category: "digital-platforms",
    subcategory: "Industrial Intelligence",
    brand: "DEBSUN",
    description:
      "Long-term industrial intelligence platform initiative focused on unifying engineering workflows, industrial operations and intelligent decision support.",
    applications: [
      "Industrial intelligence",
      "Factory operations visibility",
      "Engineering collaboration",
      "Production analytics",
    ],
    features: [
      "Unified industrial operations direction",
      "Engineering workflow focus",
      "Decision-support ambition",
      "Scalable platform roadmap",
      "Released only after engineering validation",
    ],
    specifications: [
      { label: "Status", value: "Under Development" },
      { label: "Domain", value: "Industrial intelligence" },
      { label: "Scope", value: "Platform initiative" },
      { label: "Availability", value: "Not commercially released" },
      { label: "Engagement", value: "Contact engineering team" },
      { label: "Documentation", value: "Public overview only" },
    ],
    images: [],
    downloads: defaultDownloads(),
    relatedProducts: ["DB-LYNDA-RESEARCH"],
    status: STATUS.UNDER_DEVELOPMENT,
    quoteEnabled: false,
    priceVisible: false,
    available: false,
    featured: true,
    href: "/genesys",
    tags: ["GENESYS", "Platform", "Industrial Intelligence"],
    searchKeywords: [
      "genesys",
      "industrial intelligence",
      "digital platform",
      "mes",
      "operations platform",
    ],
    price: null,
    seo: {
      title: "DEBSUN GENESYS | Platform Initiative",
      description:
        "DEBSUN GENESYS industrial intelligence platform — under development.",
    },
  },
  {
    sku: "DB-LYNDA-RESEARCH",
    slug: "research",
    familyId: "lynda",
    name: "LYNDA",
    category: "research-initiatives",
    subcategory: "Industrial AI Research",
    brand: "DEBSUN",
    description:
      "Research initiative exploring applied industrial intelligence to assist engineering diagnosis, design support and operational decision-making.",
    applications: [
      "Engineering assistance research",
      "Diagnostics support studies",
      "Decision-support exploration",
      "Industrial data interpretation research",
    ],
    features: [
      "Research preview status",
      "Industrial problem focus",
      "Engineering-first evaluation",
      "No commercial release claim",
      "Aligned with DEBSUN long-term vision",
    ],
    specifications: [
      { label: "Status", value: "Research" },
      { label: "Domain", value: "Applied industrial intelligence" },
      { label: "Release", value: "Not commercially available" },
      { label: "Engagement", value: "Research discussion on request" },
      { label: "Scope", value: "Exploratory" },
      { label: "Documentation", value: "Internal research materials" },
    ],
    images: [],
    downloads: defaultDownloads(),
    relatedProducts: ["DB-GENESYS-PLATFORM"],
    status: STATUS.RESEARCH,
    quoteEnabled: false,
    priceVisible: false,
    available: false,
    featured: true,
    tags: ["LYNDA", "Research", "Industrial AI"],
    searchKeywords: [
      "lynda",
      "research",
      "industrial ai",
      "engineering intelligence",
    ],
    price: null,
    seo: {
      title: "LYNDA Research Initiative | DEBSUN",
      description:
        "LYNDA — DEBSUN research into applied industrial intelligence.",
    },
  },
];

export function getAllProducts() {
  return PRODUCTS;
}

export function getProductBySku(sku) {
  if (!sku) return null;
  const key = String(sku).toLowerCase();
  // Legacy Corona floodlight SKU → Gamma 200W
  if (key === "db-led-fl-200" || key === "corona-floodlight-200w") {
    return PRODUCTS.find((p) => p.sku === "DB-LED-FL-200") || null;
  }
  return PRODUCTS.find((p) => p.sku.toLowerCase() === key) || null;
}

export function getProductByFamilyAndSlug(familyIdOrSlug, variantSlug) {
  if (!familyIdOrSlug || !variantSlug) return null;
  const fam = String(familyIdOrSlug).toLowerCase();
  const slug = String(variantSlug).toLowerCase();
  return (
    PRODUCTS.find(
      (p) =>
        p.slug?.toLowerCase() === slug &&
        (p.familyId?.toLowerCase() === fam ||
          // allow range slug match via familyId equality (ranges use same id)
          p.familyId?.toLowerCase() === fam)
    ) || null
  );
}

export function getProductsByCategory(categoryId) {
  return PRODUCTS.filter((p) => p.category === categoryId);
}

export function getProductsByFamily(familyId) {
  return PRODUCTS.filter((p) => p.familyId === familyId);
}

export function getFeaturedProducts() {
  return PRODUCTS.filter((p) => p.featured);
}

export function getShopProducts() {
  return PRODUCTS.filter(
    (p) =>
      p.available ||
      p.quoteEnabled ||
      p.status === STATUS.UNDER_DEVELOPMENT ||
      p.status === STATUS.RESEARCH
  );
}

export function getRelatedProducts(sku) {
  const product = getProductBySku(sku);
  if (!product) return [];
  const fromList = (product.relatedProducts || [])
    .map(getProductBySku)
    .filter(Boolean);
  if (fromList.length) return fromList;
  // Fallback: siblings in same family
  return getProductsByFamily(product.familyId).filter(
    (p) => p.sku !== product.sku
  );
}

export function searchProducts(query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return PRODUCTS;
  return PRODUCTS.filter((p) => {
    const haystack = [
      p.sku,
      p.name,
      p.slug,
      p.familyId,
      p.subcategory,
      p.description,
      p.shortDescription,
      p.wattage,
      ...(p.tags || []),
      ...(p.searchKeywords || []),
      ...(p.applications || []),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function specsToList(specifications) {
  if (!specifications) return [];
  if (Array.isArray(specifications)) {
    return specifications.map((s) =>
      typeof s === "string" ? s : `${s.label}: ${s.value}`
    );
  }
  return Object.entries(specifications).map(([k, v]) => `${k}: ${v}`);
}

/** Canonical detail URL for a product variant. */
export function getProductPath(product) {
  if (!product) return "/products";
  if (product.href) return product.href;
  if (product.familyId && product.slug) {
    return `/products/${product.familyId}/${product.slug}`;
  }
  return `/products/${product.sku}`;
}

/** Quote portal handoff URL (preserves existing workflow). */
export function getQuotePath(product, { authenticated = false } = {}) {
  if (!product?.sku) return "/portal";
  const portal = `/portal?quote=${encodeURIComponent(product.sku)}`;
  if (authenticated) return portal;
  return `/login?next=/portal&quote=${encodeURIComponent(product.sku)}`;
}

/** Build quote prefill payload for Portal NewQuoteDialog. */
export function buildQuotePrefill(product) {
  if (!product) return null;
  return {
    project_name: `${product.name} enquiry`,
    industry: "",
    notes: [
      `Product: ${product.name}`,
      `SKU: ${product.sku}`,
      product.wattage ? `Variant: ${product.wattage}` : null,
      product.familyId ? `Range: ${product.familyId}` : null,
      product.subcategory ? `Category: ${product.subcategory}` : null,
    ]
      .filter(Boolean)
      .join("\n"),
    items: [
      {
        description: `${product.name} (${product.sku})`,
        quantity: 1,
        product_id: product.sku,
      },
    ],
  };
}
